import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "ml-auto" }
const _hoisted_2 = ["textContent"]

import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import { useToast } from 'primevue/usetoast';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useClinicianStudentStore, useArchiveStore } from '@/store';
import { StudentInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can, isValidForRoleByTenant } from '@/composables';

interface StudentPropsInterface {
  tableData?: StudentInterface[];
  clinicianId?: number | undefined;
  loading?: boolean;
}

interface StudentTableInterface {
  id: number;
  display_name: string;
  service?: string;
  is_group?: boolean;
  grade?: string;
  age: number;
}

type TreatmentService = {
  service: { name: string };
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ClinicianStudentTable',
  props: {
    tableData: {},
    clinicianId: {},
    loading: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const clinicianStudentStore = useClinicianStudentStore();
const archiveStore = useArchiveStore();
const students: Ref<StudentInterface[] | undefined> = toRef(props, 'tableData');
const studentCount: Ref<number> = computed(() => (students.value ? students.value.length : 0));
const studentModalOpen = ref(false);
const addStudentDataLoading = ref(false);
const toast = useToast();
const studentItems: Ref<{ name: string; value: number }[]> = ref([]);

const createMenuModel = (item: StudentTableInterface) => {
  return [
    {
      label: 'Archive Student',
      command: () => archiveStudent(item.id, item.display_name),
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
    },
    {
      label: 'Remove Student',
      command: () => detachStudent([item.id], item.display_name),
      visible: can([PERMISSIONS.CLINICIAN_DETACH_STUDENTS]),
    },
    {
      //TODO: Add logic to show this menu item and permissions
      label: 'Edit Completed Goal',
    },
    {
      //TODO: Add logic to show this menu item and
      label: 'Expired Goal',
    },
  ].filter((item) => item.visible);
};

const archiveStudent: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  const archived_reason_id = 1;
  await archiveStore.archive(id, { archived_reason_id }).then(() => {
    emit(EMIT.UPDATE);
    doToast(false, `${student} Archived`);
  });
};

const attachStudent = async (ids: number[]) => {
  await clinicianStudentStore.attachStudents({
    students: ids,
    clinicianId: props.clinicianId as number,
  });
  emit(EMIT.UPDATE);
  doToast(false, 'Student(s) has been added');
};

const doToast = (error = false, msg: string) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: msg,
    life: 5000,
  });
};

const detachStudent = async (ids: number[], studentName: string) => {
  await clinicianStudentStore.detachStudents({ students: ids, clinicianId: props.clinicianId as number });
  emit(EMIT.UPDATE);
  doToast(false, `${studentName} has been removed from service provider`);
};

const openAddStudents = async () => {
  studentItems.value = [];
  if (can([PERMISSIONS.CLINICIAN_STUDENTS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.CLINICIAN_ATTACH_STUDENTS])) {
    addStudentDataLoading.value = true;
    studentModalOpen.value = true;
    studentItems.value = await clinicianStudentStore.getAddStudentsModalData(props.clinicianId as number);
    addStudentDataLoading.value = false;
  } else {
    doToast(true, 'You do not have permission to add students');
  }
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  const studentIds = students.map((student) => student.value);
  attachStudent(studentIds);
  studentModalOpen.value = false;
};

const treatmentServices = (treatmentServices: TreatmentService[]) => {
  const treatmentServiceNames = treatmentServices.map((item: TreatmentService) => item.service.name);
  const uniqueTreatmentServiceNames = [...new Set(treatmentServiceNames)];

  return uniqueTreatmentServiceNames.join(', ');
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_Column = _resolveComponent("Column")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Students", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(studentCount.value), 1)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).CLINICIAN_ATTACH_STUDENTS]) && _unref(isValidForRoleByTenant)())
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              type: "text",
              size: "small",
              text: "Add Student",
              "icon-left": "add",
              onClick: openAddStudents
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        value: students.value,
        "global-filter-fields": ['display_name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: __props.loading,
        "data-key": "display_name",
        "responsive-layout": "scroll",
        "scroll-height": "440px",
        "state-key": "session-store-students-table",
        "state-storage": "session",
        class: _normalizeClass({ 'table-loading': __props.loading })
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Students not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (studentCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_Column, {
                  "body-style": "max-width: 20rem",
                  field: "display_name",
                  header: "Student Name",
                  "header-style": "max-width: 20rem; color: #3B4242;",
                  sortable: ""
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_RouterLink, {
                      to: `/user/${data.id}`,
                      class: "font-semibold",
                      textContent: _toDisplayString(data.display_name)
                    }, null, 8, ["to", "textContent"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, { header: "Service" }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      textContent: _toDisplayString(treatmentServices(data.treatment_services))
                    }, null, 8, _hoisted_2)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, { header: "Grade" }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", null, _toDisplayString(data.grade ? data.grade : '-'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, { header: "Age" }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", null, _toDisplayString(data.age != 0 ? data.age : '-'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  "body-class": "justify-content-center mw-10rem",
                  "header-class": "justify-content-center mw-10rem",
                  field: "id",
                  header: "Actions"
                }, {
                  body: _withCtx(({ data }) => [
                    (createMenuModel(data).length > 0)
                      ? (_openBlock(), _createBlock(_component_ActionMenu, {
                          key: 0,
                          "menu-items": createMenuModel(data),
                          icon: "more_horiz"
                        }, null, 8, ["menu-items"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "loading", "class"]),
      (studentModalOpen.value && _unref(can)([_unref(PERMISSIONS).CLINICIAN_STUDENTS_AVAILABLE_TO_ADD]) && _unref(isValidForRoleByTenant)())
        ? (_openBlock(), _createBlock(AddPersonModal, {
            key: 0,
            title: "Add students",
            "button-text": "Add students",
            placeholder: "Search students",
            loading: addStudentDataLoading.value,
            items: studentItems.value,
            searchable: true,
            onAddItems: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["loading", "items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})